// src/logging/maskSensitiveVariables.ts
//
// Schluesselbasierte, rekursive Maskierung beliebig tief verschachtelter
// Strukturen. Liegt bewusst in src/logging und nicht mehr in
// src/server/lib/graphqlErrorLoggingPlugin.ts: der Logger und die
// Fehler-Normalisierung brauchen sie ebenfalls (#734 M4), das Plugin importiert
// aber den Logger — ein Import in die Gegenrichtung waere ein Zyklus.
// src/logging bleibt damit frei von Imports aus src/server (wie
// maskSensitiveText.ts und errorLogData.ts).

import {maskSensitiveText} from "./maskSensitiveText";

// pino-redact trifft nur feste Pfade; GraphQL-Variablen und Fehler-Payloads sind
// beliebig tief verschachtelt und die Feldnamen variieren (IBAN, senderIban,
// qrIban, creditcardNumber, refreshToken …). Deshalb: rekursiv, case-insensitiv
// und per Namensfragment maskieren. Lieber ein harmloses Feld zu viel (postcode →
// "code", capacity → "city") als ein Zahlungsdatum im Log.
//
// Zweiter Block: Personendaten aus BookerInput/BookerAttendeeInput (#734 H1).
// Bei einem Server-Fehler in createBooker/updateBooker/createBooking landete
// sonst die komplette Mutation-Payload mit Name, Geburtsdatum, Telefon und
// Adresse im Klartext in Loki. "name" ist bewusst NICHT in der Liste
// (courseName, operationName, templateName sind fuer die Diagnose wichtig),
// stattdessen die konkreten Personen-Felder.
export const SENSITIVE_KEY_FRAGMENTS = [
    "iban",
    "bic",
    "accountholder",
    "creditcard",
    "cvc",
    "password",
    "secret",
    "token",
    "code",
    // Personendaten
    "email",
    "mail",
    "firstname",
    "lastname",
    "surname",
    "phone",
    "mobile",
    "birthday",
    "dateofbirth",
    "street",
    "housenumber",
    "city",
    "address",
    "healthinsur",
    // AHV-Nummer (Tarif 595): Klartext steht nur in der Eingabe von updateAttendeeHealthInsurance
    "ahv",
    // Freitextfelder (koennen alles enthalten)
    "annotation",
    "note",
    "notice",
];

// Client-JSON kann absichtlich tief verschachtelt sein: Rekursion begrenzen
const MAX_DEPTH = 20;

export interface MaskOptions {
    /**
     * Schluessel, die trotz Fragment-Treffer im Klartext bleiben — Vergleich auf
     * den GANZEN Namen (case-insensitiv), nicht als Fragment. `code` bleibt so
     * lesbar, `postCode`/`errorCode` weiterhin maskiert.
     */
    keepKeys?: Array<string>;
}

/**
 * Diagnose-Schluessel, die auf FEHLER-Objekten erhalten bleiben.
 *
 * Die Fragmentliste ist absichtlich uebermaskierend ("code", "note", "name" als
 * Teilstring). Auf GraphQL-Variablen ist das richtig, auf Fehlerobjekten wuerde
 * es den Diagnosewert vernichten: Postgres `code` ("23505"), `constraint`,
 * `table`, `column`, `routine`, Node `errno`, HTTP `statusCode` und
 * `errorName`/`operationName` waeren alle "[redacted]" — genau die Felder, mit
 * denen man einen Prod-Fehler ueberhaupt erst zuordnet.
 *
 * Bewusst als Allowlist auf DERSELBEN Fragmentliste statt als zweiter,
 * eng gefasster Fragmentsatz: es bleibt eine Single Source of Truth fuer
 * "was ist sensibel", der Fehlerpfad hebt nur eine kurze, explizite Ausnahme
 * heraus. Ein zweiter Schluesselsatz wuerde bei jedem neuen PII-Feld
 * auseinanderlaufen.
 */
export const ERROR_DIAGNOSTIC_KEYS = [
    "code",
    "statusCode",
    "name",
    "errorName",
    "operationName",
    "errno",
    "constraint",
    "table",
    "column",
    "routine",
];

export const isSensitiveKey = (key: string, options: MaskOptions = {}): boolean => {
    const lower = key.toLowerCase();

    if (
        options.keepKeys &&
        options.keepKeys.some((keep) => keep.toLowerCase() === lower)
    ) {
        return false;
    }

    return SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.indexOf(fragment) !== -1);
};

const maskValue = (
    value: unknown,
    depth: number,
    options: MaskOptions,
    ancestors: Array<unknown>,
): unknown => {
    if (depth > MAX_DEPTH) {
        return "[too deep]";
    }
    // Datumswerte durchreichen: Object.keys(date) ist leer, die Rekursion wuerde
    // sie zu `{}` machen — in Fehler-Details (dueDate, createdAt) waere das ein
    // Diagnoseverlust gegenueber dem bisherigen JSON.stringify-Verhalten.
    if (value instanceof Date) {
        return value;
    }
    if (value && typeof value === "object") {
        // Zyklen kommen in Fehler-Payloads real vor (Axios haengt an
        // `response` wieder `config`/`request`); ohne Abbruch wuerde die
        // Rekursion den Teilbaum bis MAX_DEPTH aufblaehen.
        if (ancestors.indexOf(value) !== -1) {
            return "[circular]";
        }

        const nextAncestors = ancestors.concat([value]);

        if (Array.isArray(value)) {
            return value.map((item) => maskValue(item, depth + 1, options, nextAncestors));
        }

        const source = value as Record<string, unknown>;
        const masked: Record<string, unknown> = {};

        Object.keys(source).forEach((key) => {
            masked[key] = isSensitiveKey(key, options)
                ? "[redacted]"
                : maskValue(source[key], depth + 1, options, nextAncestors);
        });

        return masked;
    }

    // Freitext-Werte (description, subject …) koennen E-Mail oder IBAN
    // enthalten, ohne dass der Feldname das verraet.
    return maskSensitiveText(value);
};

/**
 * Maskiert sensible Felder rekursiv (bis {@link MAX_DEPTH}), anhand der
 * Schluesselnamen und zusaetzlich per Freitext-Erkennung in den Werten.
 * Gibt immer eine Kopie zurueck, die Eingabe bleibt unveraendert.
 */
export const maskSensitiveVariables = (
    value: unknown,
    depth = 0,
    options: MaskOptions = {},
): unknown => {
    return maskValue(value, depth, options, []);
};

/**
 * Variante fuer Fehler-Payloads (Error-Zusatzfelder, `details` aus
 * {@link normalizeErrorLogData}): maskiert genauso tief, laesst aber die
 * Diagnose-Schluessel aus {@link ERROR_DIAGNOSTIC_KEYS} stehen.
 */
export const maskErrorDetails = (value: unknown): unknown => {
    return maskSensitiveVariables(value, 0, {keepKeys: ERROR_DIAGNOSTIC_KEYS});
};
